/******************************************************************************\
 * :$
 *
 * Copyright(c) 2023 SAS Institute Inc., Cary, NC, USA. All Rights Reserved.
 *
 * Name: TeamComments
 *
 * Purpose:
 *
 * Author: craig (Craig.Simpson@sas.com)
 *
 * Support: SAS(r) Solutions OnDemand
 *
 * Input:
 *
 * Output:
 *
 * Parameters: (if applicable)
 *
 * Dependencies/Assumptions:
 *
 * Usage:
 *
 * History:
 * ddmmmyyyy userid description (Change Code)
 * 16May2023 craig File created.
 \******************************************************************************/
import React, {Fragment, useEffect, useState} from "react";
import {GSContext} from "../../../context/context";
import {Col, Container, Form, Row} from "react-bootstrap";
import {useParams} from "react-router-dom";
import {useRequest} from "../../../helper/useRequest";
import {
    IReviewerComment,
    ISubmission,
    ITeam
} from "../../../models/application/IApplication";
import "./TeamComments.css";
import parse from "html-react-parser";
import "../../../views/routes/submission/SubmissionPillar";

export const TeamComments: React.FC = (props) => {
    const [sessionUser] = React.useContext(GSContext);
    const {teamID} = useParams();
    const appDataURL = process.env.REACT_APP_API_URL + "/api/reviewercomments/" + teamID;
    const [teamCommentsData, setTeamCommentsData] = useState<ITeam>();
    const [requestState, getTeamComments] = useRequest({url: appDataURL, initialIsLoading: true,})
    const {isLoading, data, error} = requestState;
    const [activeSubmission, setActiveSubmission] = useState<ISubmission>();
    const [selectedSubmission, setSelectedSubmission] = useState<string>("");



    //const renderHTML = (rawHTML: string) => React.createElement("div", { dangerouslySetInnerHTML: { __html: rawHTML } });

    /**
     * call the backend to get the team comments data
     */
    useEffect(() => {
            getTeamComments();
        }, []
    )

    /**
     * if team comments data changes, set it
     */
    useEffect(() => {
            if (data != null) {
                setTeamCommentsData(data);
                setActiveSubmission(data.TeamSubmissionLinkage[0].Submission);
            }
        }, [data]
    )

    const buildSubSelect = () => {
        if (teamCommentsData !== undefined && teamCommentsData.TeamSubmissionLinkage !== undefined && teamCommentsData.TeamSubmissionLinkage.length > 0) {
            return <Form className={"form-layout"} noValidate={true}>
                <Row className="mb-3">
                    <Form.Group as={Col} className={"col-left"} controlId={"formApplication"}>
                        <Form.Label>Select Submission *</Form.Label>
                        <Form.Control as={"select"} className="text-mid-width"
                                      value={selectedSubmission}
                                      onChange={handleSubSelect}>
                            {buildSubmissionsList()}
                        </Form.Control>
                    </Form.Group>
                </Row>
            </Form>
        }
        return <div/>
    }

    const handleSubSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const subLinkage = teamCommentsData?.TeamSubmissionLinkage?.find(e => e.SubmissionID.toString() === event.target.value);
        if (subLinkage !== undefined) {
            setSelectedSubmission(event.target.value);
            setActiveSubmission(subLinkage.Submission);
        }
    }

    const buildSubmissionsList = () => {
        let optionTemplate = teamCommentsData?.TeamSubmissionLinkage?.map(v => (
            <option key={v.SubmissionID} value={v.SubmissionID}>{v.Submission.Organization}</option>
        ));

        return optionTemplate;
    }

    const BuildUserCommentsPillar = (pillar: string, reviewerComment?: IReviewerComment) => {
        if (reviewerComment !== undefined && (reviewerComment.Accolades.length > 0 || reviewerComment.Concerns.length > 0)) {
            //console.log("sub = " + reviewerComment.SubmissionID + " Pillar = " + pillar + ", user = " + reviewerComment.UserID);
            return <Row>
                <Row><Col className={"pillar-row pillar-header1"}>{getPillar(pillar)}</Col></Row>
                <Row><Col className={"pillar-sub-header"}>Accolades</Col></Row>
                <Row><Col
                    className={"pillar-all-comments"}>{reviewerComment === undefined ? "" : parse(reviewerComment.Accolades)}</Col></Row>
                <Row><Col className={"pillar-sub-header"}>Concerns</Col></Row>
                <Row><Col
                    className={"pillar-all-comments"}>{reviewerComment === undefined ? "" : parse(reviewerComment.Concerns)}</Col></Row>
            </Row>
        }
    }

    const buildUserComments = (reviewerComment?: IReviewerComment[]) => {
        if (reviewerComment !== undefined && reviewerComment !== null && reviewerComment.length > 0) {
            return <Container>
                {BuildUserCommentsPillar("P1", reviewerComment.find(c => c.Pillar === "P1"))}
                {BuildUserCommentsPillar("P2", reviewerComment.find(c => c.Pillar === "P2"))}
                {BuildUserCommentsPillar("P3", reviewerComment.find(c => c.Pillar === "P3"))}
                {BuildUserCommentsPillar("P5", reviewerComment.find(c => c.Pillar === "P5"))}
                {BuildUserCommentsPillar("ESSAY", reviewerComment.find(c => c.Pillar === "ESSAY"))}
                {BuildUserCommentsPillar("COMMENTS", reviewerComment.find(c => c.Pillar === "COMMENTS"))}
            </Container>
        }
    }


    const buildSubmissionUser = (submisison: ISubmission) => {
        let userLinkages = teamCommentsData?.TeamUserLinkage;
        if (submisison !== undefined) {
            let result = userLinkages?.map(linkage => {
                let userInfo = linkage.TeamUserSubmissionLinkage?.filter(u => {
                    return u.UserID === linkage.UserID;
                }).filter(x => {return x.SubmissionID === submisison.SubmissionID;})
                //comments = userInfo?[0].reviewerComments;
                let comments;
                if (userInfo !== undefined && userInfo.length > 0) {
                    comments = userInfo[0].ReviewerComment;
                }
                else {
                    comments = new Array(0);
                }
                return <Row>
                    <Col>
                        <Container className={"reviewer-comments"}>
                            <Row>
                                <Col className={"reviewer-info"}><i>{linkage.GSUser?.FirstName}&nbsp;{linkage.GSUser?.LastName},&nbsp;{linkage.GSUser?.Title},&nbsp;{linkage.GSUser?.Organization}</i></Col>
                                {buildUserComments(comments)}
                            </Row>
                        </Container>
                    </Col>
                </Row>
            })
            return <Container className={"user-comments-section"}>{result}</Container>
        } else {
            return <Container>No data found</Container>
        }
    }

    const buildSubmissionComments = () => {

        if (activeSubmission !== undefined) {
            let name = activeSubmission.OrgDataMap["GS_Sig_Name_FIRST_NAME"]?.ResponseList[0].Response + " " + activeSubmission.OrgDataMap["GS_Sig_Name_LAST_NAME"]?.ResponseList[0].Response;
            if (activeSubmission.OrgDataMap["GS_Sig_Name_CREDENTIALS"]?.ResponseList[0].Response !== undefined) {
                name += ", " + activeSubmission.OrgDataMap["GS_Sig_Name_CREDENTIALS"]?.ResponseList[0].Response;
            }
            return <Row className={"header-container"}>
                <Col>
                    <Container className={"submission-header-container"}>
                        <Row className={"submission-info-header"}>
                            <Col>
                                <Container>
                                    <Row>{activeSubmission.Organization}</Row>
                                    <Row>
                                        {activeSubmission.OrgDataMap["Approx_US_Employee_Count"]?.ResponseList[0].Response} Employees
                                    </Row>
                                    <Row>
                                        {activeSubmission.OrgDataMap["Approx_US_Locations"]?.ResponseList[0].Response} Location(s)
                                    </Row>
                                </Container>
                            </Col> <Col>
                            <Container>
                                <Row>{name}</Row>
                                <Row>
                                    {activeSubmission.OrgDataMap["GS_Sig_Name_TITLE"]?.ResponseList[0].Response}
                                </Row>
                                <Row></Row>
                            </Container>
                        </Col>
                            <Col>
                                <Container>
                                    <Row>{activeSubmission.OrgDataMap["NAICS_Code_1"]?.ResponseList[0]?.Response}</Row>
                                    <Row>{activeSubmission.OrgDataMap["NAICS_Code_2"]?.ResponseList[0]?.Response}</Row>
                                    <Row>{activeSubmission.OrgDataMap["NAICS_Code_3"]?.ResponseList[0]?.Response}</Row>
                                </Container>
                            </Col>
                        </Row>
                    </Container>
                </Col>
                <Container>
                    {buildSubmissionUser(activeSubmission)}
                </Container>
            </Row>
        } else {
            return <Container>No data found</Container>
        }
    }

    const buildTeamComments = () => {
        return <Container>
            {buildSubSelect()}
            {buildSubmissionComments()}
        </Container>
    }

    /**
     * Get the piller header
     *
     * @param pillarNumber
     */
    function getPillar(pillarNumber?: string) {
        if (pillarNumber === "P2") {
            return "Pillar 2: Prevention & Early Detection";
        } else if (pillarNumber === "P3") {
            return "Pillar 3/4:  Advancing Treatment & Survivorship";
        } else if (pillarNumber === "P5") {
            return "Pillar 5:  Well-Being";
        } else if (pillarNumber === "ESSAY") {
            return "Essay";
        } else if (pillarNumber === "COMMENTS") {
            return "Comments";
        } else {
            return "Pillar 1: Health Education & Navigation";
        }
    }

    return (
        <Fragment>
            {isLoading && <p>Loading ...</p>}
            {error && <p>Error</p>}
            {data && buildTeamComments()}
        </Fragment>
    );
}